import { getRouteApi } from '@tanstack/react-router'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import CancelIcon from '@/assets/icons/cancel.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import HistoryIcon from '@/assets/icons/history.svg?react'
import LinkText from '@/components/LinkText/LinkText'
import Table, {
  type Column,
  type TableProps,
  type TableAction
} from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import TableTopBarActions from '@/components/TableTopBar/TableTopBarActions'
import type useRowSelection from '@/hooks/useRowSelection'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import type { IdAndName } from '@/types/id-and-name'
import { getColumnWidth } from '@/utils/get-column-width'

import PreferredRoomsColumn from '../components/PreferredRoomsColumn'
import type { CoursesList, CoursesListItem } from '../queries/useCourses'

type CoursesManagementTableProps = {
  onArchiveClick: (row: CoursesListItem) => void
  onRemoveClick: (row: CoursesListItem) => void
  onBulkRemoveClick: (courseIds: string[]) => void
  rowSelection: ReturnType<typeof useRowSelection>
  data?: CoursesList
} & Pick<
  TableProps<CoursesListItem>,
  | 'count'
  | 'totalCount'
  | 'pagination'
  | 'isLoading'
  | 'onChangePage'
  | 'onChangePageSize'
>

const routeApi = getRouteApi('/_auth/courses-management/')

const CoursesManagementTable = (props: CoursesManagementTableProps) => {
  const navigate = routeApi.useNavigate()

  const { t } = useTranslation(['coursesManagement', 'common'])
  const { isExtraLarge } = useScreenResolution()

  const columns: Column<CoursesListItem>[] = [
    {
      dataIndex: 'displayName',
      key: 'name',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      title: t('label.course-name'),
      render: (name: string, { id }) => (
        <LinkText
          to="/courses-management/$courseId"
          params={{
            courseId: id
          }}
        >
          {name}
        </LinkText>
      )
    },
    {
      dataIndex: ['educationLevel', 'name'],
      key: 'educationLevel',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      title: t('label.education-level')
    },
    {
      dataIndex: ['domain', 'name'],
      key: 'domain',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      title: t('label.domain')
    },
    {
      dataIndex: 'preferredRooms',
      key: 'preferredRoom',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      title: t('label.preferred-rooms'),
      render: (preferredRooms: IdAndName[] | null) => (
        <PreferredRoomsColumn preferredRooms={preferredRooms} />
      )
    }
  ]

  const actions: TableAction<CoursesListItem>[] = [
    {
      icon: <EditIcon />,
      text: t('button.edit'),
      onClick: row =>
        navigate({
          to: '/courses-management/$courseId/edit',
          params: { courseId: row.id }
        })
    },
    {
      icon: <HistoryIcon />,
      onClick: props.onArchiveClick,
      text: t('button.archive-course')
    },
    {
      icon: <CancelIcon />,
      onClick: props.onRemoveClick,
      text: t('button.remove'),
      variantAction: 'danger'
    }
  ]

  const bulkActions = [
    {
      text: t('button.remove'),
      icon: <DeleteIcon />,
      onClick: () => props.onBulkRemoveClick(props.rowSelection.selectedItems)
    }
  ]

  return (
    <Table
      id="courses-management-table"
      data={props.data ?? []}
      columns={columns}
      count={props.count}
      totalCount={props.totalCount}
      actions={actions}
      rightTopBar={
        <TableTopBarActions
          selectedItemsLength={props.rowSelection.selectedItems.length}
          actions={bulkActions}
        />
      }
      rowSelection={{
        selectedItems: props.rowSelection.selectedItems,
        allCheckboxValue: props.rowSelection.getSelectAllCheckboxValue(),
        count: props.count ?? 0,
        labelHeader: selected =>
          t(
            selected ? 'label.deselect-all-courses' : 'label.select-all-courses'
          ),
        labelItem: ({ selected, displayName }) =>
          t(selected ? 'label.deselect-course' : 'label.select-course', {
            COURSE: displayName
          }),
        onSelect: props.rowSelection.onSelect,
        onSelectAll: props.rowSelection.onSelectAll
      }}
      pagination={props.pagination}
      isLoading={props.isLoading}
      onChangePage={props.onChangePage}
      onChangePageSize={props.onChangePageSize}
      caption={t('header.courses-management')}
    />
  )
}

export default memo(CoursesManagementTable)
